import apiClient from "./base/apiClient";
import { FollowStatus } from "../constants/follow";

export interface UserProfile {
  id: string;
  nickname: string; // 이름
  username: string;
  email?: string | null; // 이메일
  profile_image: string | null;
  phone: string | null; // 전화번호
  region: string | null; // 지역
  bio: string | null; // 소개
  is_public: boolean; // 프로필 공개 설정
  location_consent: boolean; // 위치 정보 이용 동의
  location_consent_at: string | null; // 위치 정보 이용 동의 일자
  contact_consent: boolean; // 연락처 연동 동의
  contact_consent_at: string | null; // 연락처 연동 동의 일자
  push_notification_consent: boolean; // 푸시 알림 동의
  push_notification_consent_at: string | null; // 푸시 알림 동의 일자
  /** 지도 '기록된 장소' 필터를 보여줄지 (서버 허용 목록. 지금은 운영진만) */
  can_use_recorded_filter?: boolean;
  review_count: number;
  follower_count: number;
  following_count: number;
  consecutive_attendance_days: number | null;
  /** 취향분석에서 가장 높게 나온 성향 코드('trend' 등). 안 풀었으면 null */
  taste_trait: string | null;
  is_social_login: boolean; // 소셜 로그인 여부
}

export interface GetUserProfileResponse {
  user: UserProfile;
}

export interface UpdateProfileRequest {
  username?: string; // 아이디
  email?: string; // 이메일
  nickname?: string;
  profile_image?: File | null;
  region?: string | null; // 지역
  bio?: string | null; // 소개
  is_public?: boolean; // 프로필 공개 설정
  location_consent?: boolean; // 위치 정보 이용 동의
  contact_consent?: boolean; // 연락처 연동 동의
  push_notification_consent?: boolean; // 푸시 알림 동의
}

export interface UpdateProfileResponse {
  message: string;
  user: {
    id: string;
    nickname: string;
    username: string;
    profile_image: string | null;
  };
}

export interface VerifySecurityAnswerRequest {
  security_answer: string;
}

export interface VerifySecurityAnswerResponse {
  message: string;
  verified: boolean;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export interface RequestFollowResponse {
  status: "pending" | "accepted" | "rejected";
  message: string;
}

export interface AcceptFollowResponse {
  status: "accepted";
  message: string;
}

export interface RejectFollowResponse {
  message: string;
}

export interface CancelFollowResponse {
  message: string;
}

export interface RemoveFollowerResponse {
  message: string;
}

export interface Follower {
  id: string;
  nickname: string;
  username: string;
  profile_image: string | null;
  status: "none" | "pending" | "accepted" | "rejected";
  is_following: boolean;
}

export interface GetFollowersResponse {
  followers: Follower[];
}

export interface FollowRequest {
  id: string;
  nickname: string;
  username: string;
  profile_image: string | null;
  status: "pending";
}

export interface GetFollowRequestsResponse {
  requests: FollowRequest[];
}

export interface Following {
  id: string;
  nickname: string;
  username: string;
  profile_image: string | null;
  /** 본인 목록: 대상에 대한 본인 팔로우 행 상태. 타 유저 목록: 조회자 기준 관계(none 포함) */
  status: "none" | "pending" | "accepted" | "rejected";
  /** 타 유저 팔로잉 목록 조회 시에만, 조회자가 해당 사용자를 팔로우 중인지 */
  is_following?: boolean;
}

export interface GetFollowingResponse {
  following: Following[];
}

export interface OtherUserProfile {
  id: string;
  nickname: string;
  username: string;
  profile_image: string | null;
  bio: string | null; // 한 줄 소개
  review_count: number;
  follower_count: number;
  following_count: number;
  follow_status: FollowStatus;
  is_private: boolean;
  similarity?: number | null;
  similarity_common_count?: number | null;
  similarity_my_saved_count?: number | null;
  categories?: ReviewCategory[];
  consecutive_attendance_days?: number | null;
  /** 취향분석에서 가장 높게 나온 성향 코드('trend' 등). 안 풀었으면 null */
  taste_trait?: string | null;
  /** 서로 팔로우 중인가. 밥약 표시 조건 */
  is_mutual_follow?: boolean;
  /** 함께한 밥약 = 내가 이 유저를 친구 태그한 리뷰 수 (일방향) */
  mealmate_count?: number | null;
}

export interface GetOtherUserProfileResponse {
  user: OtherUserProfile;
}

export interface ReviewCategory {
  id: number;
  name: string;
  thumbnail_image: string | null;
  is_public?: boolean; // 공개 여부 (false: 나만보기)
  created_at: string;
}

export interface GetReviewCategoriesResponse {
  categories: ReviewCategory[];
}

export interface FindUsersByPhoneRequest {
  phones: string[];
}

export interface FindUsersByPhoneResponse {
  results: {
    [phone: string]: {
      user: {
        id: string;
        nickname: string;
        username: string;
        profile_image: string | null;
      } | null;
      follow_status: FollowStatus;
    };
  };
}

export interface SearchMember {
  id: string;
  nickname: string;
  username: string;
  profile_image: string | null;
}

export interface SearchMembersResponse {
  users: SearchMember[];
}

export interface SavedPlaceCategory {
  category: string;
  name: string;
  count: number;
}

export interface GetSavedPlacesByCategoryResponse {
  categories: SavedPlaceCategory[];
  /** 상대 프로필을 볼 때: 나와 상대가 둘 다 저장한 곳 수 (본인 프로필이면 null) */
  common_count?: number | null;
}

/** "우리 둘 다 저장한 곳" 교집합 장소 */
export interface CommonSavedPlace {
  id: string;
  name: string;
  address: string;
  isBookmarked: boolean;
  category?: string;
  has_review?: boolean;
}

export interface BlockResponse {
  message: string;
  isBlocked: boolean;
}

export interface BlockedUser {
  id: string;
  nickname: string;
  username: string | null;
  profileImage: string | null;
  blockedAt: string | null;
}

export interface BlockedUsersResponse {
  users: BlockedUser[];
  total: number;
}

export const userApi = {
  getProfile: async (): Promise<GetUserProfileResponse> => {
    const response =
      await apiClient.get<GetUserProfileResponse>("/user/profile");
    return response.data;
  },
  updateProfile: async (
    data: UpdateProfileRequest,
  ): Promise<UpdateProfileResponse> => {
    const formData = new FormData();
    if (data.username !== undefined) {
      formData.append("username", data.username);
    }
    if (data.email !== undefined) {
      formData.append("email", data.email);
    }
    if (data.nickname !== undefined) {
      formData.append("nickname", data.nickname);
    }
    if (data.region !== undefined) {
      formData.append("region", data.region || "");
    }
    if (data.bio !== undefined) {
      formData.append("bio", data.bio || "");
    }
    if (data.profile_image) {
      formData.append("profile_image", data.profile_image);
    }
    if (data.is_public !== undefined) {
      formData.append("is_public", data.is_public.toString());
    }
    if (data.location_consent !== undefined) {
      formData.append("location_consent", data.location_consent.toString());
    }
    if (data.contact_consent !== undefined) {
      formData.append("contact_consent", data.contact_consent.toString());
    }
    if (data.push_notification_consent !== undefined) {
      formData.append(
        "push_notification_consent",
        data.push_notification_consent.toString(),
      );
    }

    const response = await apiClient.post<UpdateProfileResponse>(
      "/user/profile",
      formData,
    );
    return response.data;
  },
  updateProfilePublic: async (
    isPublic: boolean,
  ): Promise<UpdateProfileResponse> => {
    const formData = new FormData();
    formData.append("is_public", isPublic.toString());

    const response = await apiClient.post<UpdateProfileResponse>(
      "/user/profile",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },
  updateLocationConsent: async (
    consent: boolean,
  ): Promise<UpdateProfileResponse> => {
    const formData = new FormData();
    formData.append("location_consent", consent.toString());

    const response = await apiClient.post<UpdateProfileResponse>(
      "/user/profile",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },
  updateContactConsent: async (
    consent: boolean,
  ): Promise<UpdateProfileResponse> => {
    const formData = new FormData();
    formData.append("contact_consent", consent.toString());

    const response = await apiClient.post<UpdateProfileResponse>(
      "/user/profile",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },
  /**
   * 휴대폰 알림 팝업 결과(허용/거부)를 남긴다. 세기 위한 값이다.
   * 광고성 푸시 동의(updatePushNotificationConsent)와 다르다 — 서버도 그 값은 안 건드린다.
   */
  reportNotificationPermission: async (
    granted: boolean,
    source: "signup" | "home",
  ): Promise<void> => {
    await apiClient.post("/user/notification-permission", { granted, source });
  },
  updatePushNotificationConsent: async (
    consent: boolean,
  ): Promise<UpdateProfileResponse> => {
    const formData = new FormData();
    formData.append("push_notification_consent", consent.toString());

    const response = await apiClient.post<UpdateProfileResponse>(
      "/user/profile",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },
  verifySecurityAnswer: async (
    data: VerifySecurityAnswerRequest,
  ): Promise<VerifySecurityAnswerResponse> => {
    const response = await apiClient.post<VerifySecurityAnswerResponse>(
      "/user/verify-security-answer",
      data,
    );
    return response.data;
  },
  changePassword: async (
    data: ChangePasswordRequest,
  ): Promise<ChangePasswordResponse> => {
    const response = await apiClient.post<ChangePasswordResponse>(
      "/user/change-password",
      data,
    );
    return response.data;
  },
  requestFollow: async (userId: string): Promise<RequestFollowResponse> => {
    const response = await apiClient.post<RequestFollowResponse>(
      `/user/${userId}/follow`,
    );
    return response.data;
  },
  acceptFollow: async (followerId: string): Promise<AcceptFollowResponse> => {
    const response = await apiClient.post<AcceptFollowResponse>(
      `/user/${followerId}/accept`,
    );
    return response.data;
  },
  rejectFollow: async (followerId: string): Promise<RejectFollowResponse> => {
    const response = await apiClient.post<RejectFollowResponse>(
      `/user/${followerId}/reject`,
    );
    return response.data;
  },
  cancelFollow: async (userId: string): Promise<CancelFollowResponse> => {
    const response = await apiClient.delete<CancelFollowResponse>(
      `/user/${userId}/follow`,
    );
    return response.data;
  },
  getFollowers: async (userId?: string): Promise<GetFollowersResponse> => {
    const url = userId ? `/user/${userId}/followers` : "/user/followers";
    const response = await apiClient.get<GetFollowersResponse>(url);
    return response.data;
  },
  getFollowRequests: async (): Promise<GetFollowRequestsResponse> => {
    const response = await apiClient.get<GetFollowRequestsResponse>(
      "/user/follow-requests",
    );
    return response.data;
  },
  removeFollower: async (
    followerId: string,
  ): Promise<RemoveFollowerResponse> => {
    const response = await apiClient.delete<RemoveFollowerResponse>(
      `/user/${followerId}/follower`,
    );
    return response.data;
  },
  getFollowing: async (userId?: string): Promise<GetFollowingResponse> => {
    const url = userId ? `/user/${userId}/following` : "/user/following";
    const response = await apiClient.get<GetFollowingResponse>(url);
    return response.data;
  },
  getOtherUserProfile: async (
    userId: string,
  ): Promise<GetOtherUserProfileResponse> => {
    const response = await apiClient.get<GetOtherUserProfileResponse>(
      `/user/${userId}/profile`,
    );
    return response.data;
  },
  getReviewCategories: async (
    userId?: string,
  ): Promise<GetReviewCategoriesResponse> => {
    const url = userId ? `/review-categories/${userId}` : "/review-categories";
    const response = await apiClient.get<GetReviewCategoriesResponse>(url);
    return response.data;
  },
  saveFcmToken: async (token: string): Promise<void> => {
    await apiClient.post("/user/fcm-token", {
      token,
    });
  },
  deleteFcmToken: async (token: string): Promise<void> => {
    // axios delete는 body를 config.data로 전달
    await apiClient.delete("/user/fcm-token", {
      data: { token },
    });
  },
  deleteAccount: async (): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      "/user/account",
    );
    return response.data;
  },
  findUsersByPhone: async (
    data: FindUsersByPhoneRequest,
  ): Promise<FindUsersByPhoneResponse> => {
    const formData = new FormData();
    data.phones.forEach((phone) => {
      formData.append("phones[]", phone);
    });

    const response = await apiClient.post<FindUsersByPhoneResponse>(
      "/user/find-users-by-phone",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },
  searchMembers: async (
    keyword: string,
    signal?: AbortSignal,
  ): Promise<SearchMembersResponse> => {
    const response = await apiClient.get<SearchMembersResponse>(
      "/user/search",
      {
        params: { keyword },
        signal,
      },
    );
    return response.data;
  },
  getSavedPlacesByCategory: async (
    userId: string,
  ): Promise<GetSavedPlacesByCategoryResponse> => {
    const response = await apiClient.get<GetSavedPlacesByCategoryResponse>(
      `/user/${userId}/saved-places`,
    );
    return response.data;
  },
  /** 나와 상대가 둘 다 저장한 장소(교집합) 목록 */
  getCommonSavedPlaces: async (
    userId: string,
  ): Promise<{ places: CommonSavedPlace[] }> => {
    const response = await apiClient.get<{ places: CommonSavedPlace[] }>(
      `/user/${userId}/common-saved-places`,
    );
    return response.data;
  },
  /**
   * 차단한다.
   *
   * 서버가 서로의 팔로우를 함께 끊으므로, 부른 쪽은 팔로우 상태도 다시
   * 읽어야 한다. 차단 뒤에 상대 프로필은 404가 되니 목록으로 빠져나가는 편이 맞다.
   */
  blockUser: async (userId: string): Promise<BlockResponse> => {
    const response = await apiClient.post<BlockResponse>(
      `/user/${userId}/block`,
    );
    return response.data;
  },
  /** 차단을 푼다. 끊어진 팔로우는 되살아나지 않는다. */
  unblockUser: async (userId: string): Promise<BlockResponse> => {
    const response = await apiClient.delete<BlockResponse>(
      `/user/${userId}/block`,
    );
    return response.data;
  },
  /**
   * 사람을 신고한다. 기록 신고(reviewApi.report)와 다른 통로다.
   *
   * 두 번 신고해도 성공으로 돌아온다 — 이미 신고했다는 사실을 화면에
   * 알릴 이유가 없어서다.
   */
  reportUser: async (
    userId: string,
    data: {
      reason: "spam" | "inappropriate" | "harassment" | "other";
      description?: string | null;
    },
  ): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/user/${userId}/report`,
      data,
    );
    return response.data;
  },
  /** 내가 차단한 사람 목록. 나를 차단한 사람은 들어 있지 않다. */
  getBlockedUsers: async (): Promise<BlockedUsersResponse> => {
    const response =
      await apiClient.get<BlockedUsersResponse>("/user/blocks");
    return response.data;
  },
};
